import { Link } from "react-router-dom";
import { Clock, ArrowUpRight } from "lucide-react";

function catLabel(slug) {
  return (slug || "").replace(/-/g, " ").toUpperCase();
}
const FALLBACK = "https://images.unsplash.com/photo-1498050108023-c5249f4df085?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

function Category({ children }) {
  return (
    <span className="relative inline-block font-cond font-bold uppercase tracking-[0.22em] text-brand text-xs">
      {children}
      <span className="absolute left-0 -bottom-1 w-full h-0.5 bg-brand scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
    </span>
  );
}

export default function ArticleCard({ post, variant = "default" }) {
  if (!post) return null;
  const img = post.featured_image || FALLBACK;
  const to = `/article/${post.slug}`;
  const testid = `article-card-${post.slug}`;

  if (variant === "hero") {
    return (
      <Link to={to} data-testid={testid} className="group relative block overflow-hidden border border-ink-600 edge-card h-full min-h-[440px] lg:min-h-[560px]">
        <div className="absolute inset-0 overflow-hidden">
          <img src={img} alt={post.featured_image_alt || post.title} loading="lazy" className="w-full h-full object-cover img-zoom duotone" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/40 to-transparent" />
        </div>
        <div className="relative z-10 h-full flex flex-col justify-end p-7 lg:p-10">
          <div className="mb-4"><Category>{catLabel(post.category)}</Category></div>
          <h2 className="font-poster uppercase text-white leading-[0.92] text-3xl lg:text-5xl max-w-2xl">{post.title}</h2>
          {post.excerpt && <p className="text-white/70 mt-4 max-w-xl text-sm lg:text-base line-clamp-2">{post.excerpt}</p>}
          <div className="flex items-center gap-4 mt-6 font-cond uppercase tracking-wider text-xs text-white/60">
            <span>{post.author}</span><span className="w-1 h-1 bg-brand rounded-full" /><span className="flex items-center gap-1"><Clock size={12} /> {post.reading_time} MIN</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "horizontal") {
    return (
      <Link to={to} data-testid={testid} className="group flex gap-5 items-stretch border border-ink-600 bg-ink-900 edge-card">
        <div className="w-28 sm:w-40 shrink-0 overflow-hidden"><img src={img} alt={post.featured_image_alt || post.title} loading="lazy" className="w-full h-full object-cover img-zoom duotone" /></div>
        <div className="min-w-0 py-4 pr-4 flex flex-col justify-center">
          <div className="mb-2"><Category>{catLabel(post.category)}</Category></div>
          <h3 className="font-display font-bold uppercase text-white text-base sm:text-xl leading-tight line-clamp-2 group-hover:text-brand transition-colors">{post.title}</h3>
          <div className="flex items-center gap-2 mt-3 font-cond uppercase tracking-wider text-[11px] text-white/50">
            <span>{post.author}</span><span className="flex items-center gap-1"><Clock size={11} /> {post.reading_time}M</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <Link to={to} data-testid={testid} className="group grid md:grid-cols-2 border border-ink-600 bg-ink-900 edge-card overflow-hidden">
        <div className="h-60 md:h-full overflow-hidden"><img src={img} alt={post.featured_image_alt || post.title} loading="lazy" className="w-full h-full object-cover img-zoom duotone" /></div>
        <div className="p-7 lg:p-9 flex flex-col justify-center">
          <div className="mb-3"><Category>{catLabel(post.category)}</Category></div>
          <h3 className="font-poster uppercase text-white leading-[0.94] text-2xl lg:text-3xl">{post.title}</h3>
          {post.excerpt && <p className="text-white/60 mt-3 text-sm line-clamp-3">{post.excerpt}</p>}
          <div className="flex items-center gap-3 mt-5 font-cond uppercase tracking-wider text-[11px] text-white/50">
            <span>{post.author}</span><span className="flex items-center gap-1"><Clock size={11} /> {post.reading_time} MIN READ</span>
            <ArrowUpRight size={16} className="text-brand ml-auto group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={to} data-testid={testid} className="group flex flex-col border border-ink-600 bg-ink-900 edge-card h-full">
      <div className="h-52 overflow-hidden"><img src={img} alt={post.featured_image_alt || post.title} loading="lazy" className="w-full h-full object-cover img-zoom duotone" /></div>
      <div className="p-5 flex flex-col flex-1">
        <div className="mb-2.5"><Category>{catLabel(post.category)}</Category></div>
        <h3 className="font-display font-bold uppercase text-white text-lg lg:text-xl leading-tight line-clamp-3 group-hover:text-brand transition-colors">{post.title}</h3>
        {post.excerpt && <p className="text-white/55 mt-2 text-sm line-clamp-2 flex-1">{post.excerpt}</p>}
        <div className="flex items-center gap-2 mt-4 font-cond uppercase tracking-wider text-[11px] text-white/50">
          <span>{post.author}</span><span className="flex items-center gap-1"><Clock size={11} /> {post.reading_time}M</span>
          <ArrowUpRight size={15} className="text-brand ml-auto group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}
